/**
 * Database functions raise errors as: message = STABLE_CODE, hint = Spanish copy.
 * Edge Functions respond with { error: { code, message } }.
 * This module normalizes both (and network failures) into AppError.
 */
export class AppError extends Error {
  readonly code: string
  readonly details?: unknown
  constructor(code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.details = details
  }
}

const COPY: Record<string, string> = {
  NETWORK: 'No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.',
  UNAUTHENTICATED: 'Tu sesión expiró. Inicia sesión nuevamente.',
  FORBIDDEN: 'No tienes permisos para realizar esta acción.',
  SLOT_UNAVAILABLE: 'Ese horario acaba de ocuparse. Elige otro, por favor.',
  RATE_LIMITED: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
  CAPTCHA_FAILED: 'No pudimos verificar que eres una persona. Vuelve a intentarlo.',
  INVALID_TOKEN: 'El enlace no es válido o ha expirado.',
  '23505': 'Ya existe un registro con esos datos.',
  '23503': 'No se puede completar porque hay información relacionada.',
  '23P01': 'Ese horario se cruza con otro ya ocupado.',
  '42501': 'No tienes permisos para realizar esta acción.',
  PGRST116: 'No encontramos lo que buscas.',
}

export function friendlyMessage(code: string, fallback?: string | null): string {
  return COPY[code] ?? fallback ?? 'Ocurrió un error inesperado. Inténtalo de nuevo.'
}

interface PgLikeError {
  message?: string
  code?: string
  hint?: string | null
  details?: string | null
}

const APP_CODE = /^[A-Z][A-Z0-9_]{2,}$/

export function toAppError(err: unknown): AppError {
  if (err instanceof AppError) return err
  if (err instanceof TypeError && /fetch|network|Failed/i.test(err.message)) {
    return new AppError('NETWORK', friendlyMessage('NETWORK'))
  }
  if (err && typeof err === 'object') {
    const e = err as PgLikeError
    if (e.message && APP_CODE.test(e.message)) {
      return new AppError(e.message, friendlyMessage(e.message, e.hint), e.details)
    }
    if (e.code && COPY[e.code]) {
      return new AppError(e.code, friendlyMessage(e.code), e.details)
    }
    if (e.message) {
      if (/Failed to fetch|NetworkError|Load failed/i.test(e.message)) {
        return new AppError('NETWORK', friendlyMessage('NETWORK'))
      }
      return new AppError(e.code ?? 'UNKNOWN', friendlyMessage('UNKNOWN', null), e.message)
    }
  }
  return new AppError('UNKNOWN', friendlyMessage('UNKNOWN'))
}

/** Unwraps a supabase-js `{ data, error }` result, throwing AppError on failure. */
export function unwrap<T>(result: { data: T; error: unknown }): T {
  if (result.error) throw toAppError(result.error)
  return result.data
}
